import React, { useMemo, useState, useEffect, useRef } from 'react';
import { arr } from '@/features/shared/lib/utils';

function formatYyyymm(yyyymm) {
  if (!yyyymm) return '';
  const s = String(yyyymm);
  if (s.length < 6) return s;
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  return `${y}.${m}`;
}

function statusLabel(status) {
  const s = (status || '').toLowerCase();
  if (s === 'in') return '등재';
  if (s === 'out') return '탈락';
  return '';
}

function Card({ title, colorText, colorBg, items, gubun }) {
  const cardRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(0);

  const hasItems = items && items.length > 0;
  const isWos = gubun === 'wos';
  const isKci = gubun === 'kci';

  // ResizeObserver to track individual card width
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCardWidth(entry.contentRect.width);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 정렬: 날짜 내림차순 (최신순)
  const sortedItems = hasItems ? [...items].sort((a, b) => Number(b?.register_yyyymm || 0) - Number(a?.register_yyyymm || 0)) : [];

  const isSmall = cardWidth > 0 && cardWidth < 205;
  const paddingClass = isSmall ? 'p-3' : 'p-5';
  const fontSizeClass = isSmall ? 'text-[12px]' : 'text-[14px]';
  const titleFontSizeClass = isSmall ? 'text-[14px]' : 'text-[15px]';

  return (
    <div
      ref={cardRef}
      className={`w-[calc(50%-8px)] ${paddingClass} border border-[#E4E8F1] rounded-xl xl:w-[calc((100%-90px)/4)] lg:p-3 lg:min-h-[140px] lg:flex lg:flex-col items-start ${hasItems ? '' : 'opacity-50'}`}
    >
      <div
        className={`journal-indexing-tit mb-5 py-2 px-[14px] text-[12px] rounded-lg leading-[100%] lg:mb-7 ${titleFontSizeClass} lg:px-[20px] lg:py-[10px]`}
        style={{ color: colorText, background: colorBg }}
      >
        {title}
      </div>

      <div className={`flex flex-col gap-1 w-full lg:px-3 ${fontSizeClass}`}>
        {hasItems ? (
          sortedItems.map((item, idx) => {
            const dbName = item?.register_db_name || item?.register_db_type || '';
            const status = statusLabel(item?.register_status);
            const date = formatYyyymm(item?.register_yyyymm);
            // Web of Science는 대문자로, KCI는 원본 그대로, Scopus는 이름이 없으면 표시 안함
            const displayName = isWos ? (dbName ? dbName.toUpperCase() : '') : (isKci ? dbName : '');

            return (
              <div key={idx} className={`flex items-center w-full ${displayName ? 'justify-between' : 'justify-center'} gap-1`}>
                {displayName ? (
                  <span className="shrink-0" style={{ color: isWos || isKci ? colorText : undefined }}>{displayName}</span>
                ) : null}
                <span className="shrink-0 whitespace-nowrap">{status} - {date}</span>
              </div>
            );
          })
        ) : (
          <div className="flex justify-between items-center w-full">
            <div className="lg:w-full">등재 이력이 없습니다.</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function JournalIndexingInfo({ dbRegister }) {
  const data = useMemo(() => arr(dbRegister), [dbRegister]);

  // 각 DB별로 모든 이력 가져오기
  const wosItems = useMemo(() => {
    return arr(data).filter(r => (r?.register_db_gubun || '').toLowerCase() === 'wos');
  }, [data]);

  const scopusItems = useMemo(() => {
    return arr(data).filter(r => (r?.register_db_gubun || '').toLowerCase() === 'scopus');
  }, [data]);

  const natureItems = useMemo(() => {
    return arr(data).filter(r => (r?.register_db_gubun || '').toLowerCase() === 'nature');
  }, [data]);

  const kciItems = useMemo(() => {
    return arr(data).filter(r => (r?.register_db_gubun || '').toLowerCase() === 'kci');
  }, [data]);

  return (
    <div className="journal_indexing-cont flex flex-wrap gap-4 text-[14px] font-bold text-center md:flex-nowrap lg:flex-wrap xl:flex-nowrap xl:gap-[30px] lg:text-[16px]">
      <Card title="Web of Science" colorText="#532EB3" colorBg="#EDE7FF" items={wosItems} gubun="wos" />
      <Card title="Scopus" colorText="#F68B3F" colorBg="#FFE4D1" items={scopusItems} gubun="scopus" />
      <Card title="Nature Index" colorText="#555555" colorBg="#F3F5F9" items={natureItems} gubun="nature" />
      <Card title="KCI" colorText="#376BDB" colorBg="#ECF2FF" items={kciItems} gubun="kci" />
    </div>
  );
}
